import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';

import { BlacklistedModal, Spinner } from '@components';
import { UserFlow } from '@constants/generic';
import { UserTypes } from '@constants/role';
import { AUTH } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useLazyGetOrganizationListQuery } from '@features/authentication/queries/AuthQuery';
import DynamicBannerCarousel from '@features/dynamic-components/components/banner/DynamicBannerCarousel';
import DynamicPopupManager from '@features/dynamic-components/components/popup/DynamicPopupManager';
import useDynamicComponents from '@features/dynamic-components/hooks/useDynamicComponents';
import { useLazyGetCurrentSubscriptionQuery } from '@features/subscription/queries';
import TrialModeBanner from '@features/trial-mode/components/TrialModeBanner';
import { isOrgChurned, isOrgRestricted } from '@helpers/orgRestriction';
import useNotify from '@hooks/notify';
import ApprovalGuard from '@layouts/approval-guard/ApprovalGuard';
import SandboxBanner from '@layouts/main-header/components/SandboxBanner';
import MainHeader from '@layouts/main-header/MainHeader';
import {
  setCurrentSubscription,
  setRestrictionModal
} from '@layouts/main-header/userSlice';
import MobileLayout from '@layouts/mobile-layout/MobileLayout';
import { SwitchAccountModal } from '@layouts/side-nav-bar/components';
import SideNavBar from '@layouts/side-nav-bar/SideNavBar';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/storage';
import { Org } from 'types/organization';

import AccessGuard from '../access-guard/AccessGuard';
import useContentTopOffset from './hooks/useContentTopOffset';
import useTopBannersOffset from './hooks/useTopBannersOffset';

const MainLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createErrorAlert } = useNotify();

  const token = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  const orgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  const userType = getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_TYPE);

  const { currentOrg: currentStoredOrg, restrictionModal } = useSelector(
    (state: any) => state.user
  );

  const contentTopOffset = useContentTopOffset();
  const { total: topBannersOffset } = useTopBannersOffset();

  useDynamicComponents();

  const [orgList, setOrgList] = useState<Org[]>();
  const [showSwitchAccountModal, setShowSwitchAccountModal] =
    useState<boolean>(false);

  const [getCurrentSubscription, { data: subscriptionData }] =
    useLazyGetCurrentSubscriptionQuery();
  const [getOrgList] = useLazyGetOrganizationListQuery();

  const handleGetCurrentSubscription = async () => {
    const res = await getCurrentSubscription({ organizationId: orgId });
    if ('data' in res) {
      dispatch(setCurrentSubscription(res.data));
    }
  };

  const getOrgListData = async () => {
    const resp = await getOrgList(null, false);
    if ('data' in resp) {
      setOrgList(resp.data);
    } else if ('error' in resp) {
      createErrorAlert('Error in fetching organization list');
    }
  };

  const isSandBoxEnv = process.env.REACT_APP_ENVIRONMENT === 'sandbox';

  useEffect(() => {
    if (orgId) handleGetCurrentSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!token) return;
    getOrgListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoredOrg]);

  const currentOrg = orgList?.find((org) => org.id === orgId);

  useEffect(() => {
    if (!orgList) return;
    if (orgList.length <= 0 && userType !== UserTypes.ADMIN) {
      // AccountSelection requires USER_FLOW; set it for empty-org onboarding.
      if (!getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_FLOW)) {
        setLocalStorageItem(
          LOCAL_STORAGE_KEYS.USER_FLOW,
          UserFlow.USER_ONBOARD
        );
      }
      navigate(AUTH.SELECT_ACCOUNT);
      return;
    }
    // Covers both restriction reasons: blacklisted and churned.
    if (isOrgRestricted(currentOrg)) {
      dispatch(
        setRestrictionModal({
          orgList
        })
      );
    } else if (restrictionModal) {
      // Active org is healthy (e.g. after switching) — drop any blacklist UI.
      dispatch(setRestrictionModal(null));
      setShowSwitchAccountModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgList, orgId]);

  const handleSwitchAccountRequest = () => {
    setShowSwitchAccountModal(true);
  };

  // Churn is a separate flag from access_mode, so the reason for the restriction
  // is derived from the restricted org itself rather than from the slice payload.
  const restrictedOrg =
    restrictionModal?.orgList?.find((org: Org) => org.id === orgId) ||
    currentOrg;
  // Churned orgs are told about it by the API-driven churn popup, so only the
  // blacklist reason still renders a hardcoded modal.
  const showBlacklistedModal =
    !!restrictionModal && !isOrgChurned(restrictedOrg);

  return (
    <AccessGuard>
      <DynamicPopupManager
        orgList={orgList || []}
        onSwitchAccount={handleSwitchAccountRequest}
      />
      <div className="z-100 hidden h-screen w-screen overflow-hidden mobile:flex">
        <MobileLayout />
      </div>
      <div className="flex h-screen w-screen overflow-hidden mobile:hidden">
        <div className="size-full pl-55">
          {isSandBoxEnv && <SandboxBanner />}
          <TrialModeBanner />
          <div
            className="fixed left-0 z-30 h-14.5 w-55"
            style={{ top: topBannersOffset }}
          >
            <MainHeader
              orgList={orgList || []}
              subscriptionData={subscriptionData}
            />
            <SideNavBar
              orgList={orgList || []}
              subscriptionData={subscriptionData}
            />
          </div>
          <DynamicBannerCarousel />
          <div
            className="overflow-y-auto bg-cofeeNeutralBg pb-21"
            style={{
              marginTop: contentTopOffset,
              height: `calc(100vh - ${contentTopOffset}px)`
            }}
          >
            <div className="container">
              <Suspense
                fallback={
                  <div className="absolute left-1/2 top-1/2 flex size-full -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                    <Spinner />
                  </div>
                }
              >
                <ApprovalGuard>
                  <Outlet />
                </ApprovalGuard>
              </Suspense>
            </div>
          </div>
        </div>
      </div>
      {showBlacklistedModal && (
        <BlacklistedModal
          isOpen
          orgList={restrictionModal.orgList || []}
          onSwitchAccount={handleSwitchAccountRequest}
        />
      )}
      {showSwitchAccountModal && (
        <SwitchAccountModal
          showSwitchAccountModal={showSwitchAccountModal}
          onCancelModal={() => setShowSwitchAccountModal(false)}
          onCloseAfterSwitch={() => setShowSwitchAccountModal(false)}
          orgList={restrictionModal?.orgList || orgList || []}
        />
      )}
    </AccessGuard>
  );
};
export default MainLayout;
